import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFacility } from '../contexts/FacilityContext';
import './Layout.css';

function Layout({ children }) {
  const { user, logout } = useAuth();
  const { selectedFacility } = useFacility();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [revenueOpen, setRevenueOpen] = useState(false);

  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isAdminRoute    = location.pathname.startsWith('/admin');
  const isClaimsRoute   = ['/claims', '/worklist', '/holds', '/sync-health'].includes(location.pathname);
  const isRevenueRoute  = location.pathname.startsWith('/revenue');
  const isAdmin = user?.role === 'admin';

  // Auto-expand dropdowns when on their routes
  useEffect(() => {
    if (isSettingsRoute && !settingsOpen) setSettingsOpen(true);
    if (isAdminRoute && !adminOpen) setAdminOpen(true);
    if (isClaimsRoute && selectedFacility && !claimsOpen) setClaimsOpen(true);
    if (isRevenueRoute && !revenueOpen) setRevenueOpen(true);
  }, [isSettingsRoute, isAdminRoute, isClaimsRoute, isRevenueRoute, selectedFacility]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSettingsClick = () => {
    if (sidebarCollapsed) {
      navigate('/settings/account');
    } else {
      setSettingsOpen(!settingsOpen);
    }
  };

  const handleAdminClick = () => {
    if (sidebarCollapsed) {
      navigate('/admin/users');
    } else {
      setAdminOpen(!adminOpen);
    }
  };

  const handleClaimsClick = () => {
    if (!selectedFacility) {
      navigate('/claims');
      return;
    }
    if (sidebarCollapsed) {
      navigate('/claims');
    } else {
      setClaimsOpen(!claimsOpen);
    }
  };

  const handleRevenueClick = () => {
    if (sidebarCollapsed) {
      navigate('/revenue');
    } else {
      setRevenueOpen(!revenueOpen);
    }
  };

  const navItems = [
    {
      label: 'Facilities',
      path: '/',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      label: 'Patients',
      path: '/patients',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
  ];

  const settingsSubItems = [
    { label: 'Account', path: '/settings/account' },
    { label: 'MFA', path: '/settings/mfa' },
    { label: 'Billing', path: '/settings/billing' },
    { label: 'CPT Codes', path: '/settings/cpt-codes' },
  ];

  const adminSubItems = [
    { label: 'Active Users', path: '/admin/users' },
    { label: 'Deleted Users', path: '/admin/deleted-users' },
    { label: 'Merge Patients', path: '/admin/merge-patients' },
    { label: 'Patient Reviews', path: '/admin/patient-reviews' },
    { label: 'Prior Auth Rules', path: '/admin/prior-auth-rules' },
    { label: 'Audit Logs', path: '/admin/audit-logs' },
  ];

  const claimsSubItems = [
    { label: 'Claims Ledger', path: '/claims' },
    { label: 'Worklist', path: '/worklist' },
    { label: 'Holds', path: '/holds' },
    { label: 'Sync Health', path: '/sync-health' },
  ];

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="brand-logo-wrapper">
              <img
                src="/hd-plus-rcm-logo.png"
                alt="HD Plus RCM"
                className="brand-logo"
              />
            </div>
            {!sidebarCollapsed && (
              <span className="brand-name">Revenue Cycle Management</span>
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sidebarCollapsed ? (
                <polyline points="9 18 15 12 9 6" />
              ) : (
                <polyline points="15 18 9 12 15 6" />
              )}
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${location.pathname === item.path ? 'active' : ''}`}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
            </Link>
          ))}

          {/* Claims — simple link or dropdown depending on facility selection */}
          {selectedFacility ? (
            <div className="sidebar-nav-group">
              <button
                className={`sidebar-nav-item ${isClaimsRoute ? 'active' : ''}`}
                onClick={handleClaimsClick}
                title={sidebarCollapsed ? 'Claims' : undefined}
              >
                <span className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </span>
                {!sidebarCollapsed && (
                  <>
                    <span className="nav-label">Claims</span>
                    <span className={`nav-chevron ${claimsOpen ? 'open' : ''}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </>
                )}
              </button>

              {!sidebarCollapsed && (
                <div className={`sidebar-subnav ${claimsOpen ? 'open' : ''}`}>
                  {claimsSubItems.map((sub) => (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      className={`sidebar-subnav-item ${location.pathname === sub.path ? 'active' : ''}`}
                    >
                      <span className="subnav-dot" />
                      <span>{sub.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/claims"
              className={`sidebar-nav-item ${location.pathname === '/claims' ? 'active' : ''}`}
              title={sidebarCollapsed ? 'Claims' : undefined}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </span>
              {!sidebarCollapsed && <span className="nav-label">Claims</span>}
            </Link>
          )}

          {/* Revenue — dropdown with Payments subnav */}
          <div className="sidebar-nav-group">
            <button
              className={`sidebar-nav-item ${isRevenueRoute ? 'active' : ''}`}
              onClick={handleRevenueClick}
              title={sidebarCollapsed ? 'Revenue' : undefined}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </span>
              {!sidebarCollapsed && (
                <>
                  <span className="nav-label">Revenue</span>
                  <span className={`nav-chevron ${revenueOpen ? 'open' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </>
              )}
            </button>

            {!sidebarCollapsed && (
              <div className={`sidebar-subnav ${revenueOpen ? 'open' : ''}`}>
                {[
                  { label: 'Dashboard',       path: '/revenue' },
                  { label: 'A/R',             path: '/revenue/ar' },
                  { label: 'Payments',        path: '/revenue/payments' },
                  { label: 'Credit Balances', path: '/revenue/credit-balances' },
                  { label: 'Clearinghouse',   path: '/revenue/clearinghouse' },
                ].map((sub) => (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    className={`sidebar-subnav-item ${location.pathname === sub.path ? 'active' : ''}`}
                  >
                    <span className="subnav-dot" />
                    <span>{sub.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Clients */}
          <Link
            to="/clients"
            className={`sidebar-nav-item ${location.pathname.startsWith('/clients') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Clients' : undefined}
          >
            <span className="nav-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            {!sidebarCollapsed && <span className="nav-label">Clients</span>}
          </Link>

          {/* Payers */}
          <Link
            to="/payers"
            className={`sidebar-nav-item ${location.pathname === '/payers' ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Payers' : undefined}
          >
            <span className="nav-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
            </span>
            {!sidebarCollapsed && <span className="nav-label">Payers</span>}
          </Link>

          {/* Providers */}
          <Link
            to="/providers"
            className={`sidebar-nav-item ${location.pathname === '/providers' ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Providers' : undefined}
          >
            <span className="nav-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <polyline points="17 11 19 13 23 9"/>
              </svg>
            </span>
            {!sidebarCollapsed && <span className="nav-label">Providers</span>}
          </Link>

          {/* Settings with dropdown */}
          <div className="sidebar-nav-group">
            <button
              className={`sidebar-nav-item ${isSettingsRoute ? 'active' : ''}`}
              onClick={handleSettingsClick}
              title={sidebarCollapsed ? 'Settings' : undefined}
            >
              <span className="nav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </span>
              {!sidebarCollapsed && (
                <>
                  <span className="nav-label">Settings</span>
                  <span className={`nav-chevron ${settingsOpen ? 'open' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </>
              )}
            </button>

            {!sidebarCollapsed && (
              <div className={`sidebar-subnav ${settingsOpen ? 'open' : ''}`}>
                {settingsSubItems.map((sub) => (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    className={`sidebar-subnav-item ${location.pathname === sub.path ? 'active' : ''}`}
                  >
                    <span className="subnav-dot" />
                    <span>{sub.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Admin with dropdown (admin role only) */}
          {isAdmin && (
            <div className="sidebar-nav-group">
              <button
                className={`sidebar-nav-item ${isAdminRoute ? 'active' : ''}`}
                onClick={handleAdminClick}
                title={sidebarCollapsed ? 'Admin' : undefined}
              >
                <span className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </span>
                {!sidebarCollapsed && (
                  <>
                    <span className="nav-label">Admin</span>
                    <span className={`nav-chevron ${adminOpen ? 'open' : ''}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </>
                )}
              </button>

              {!sidebarCollapsed && (
                <div className={`sidebar-subnav ${adminOpen ? 'open' : ''}`} style={{ maxHeight: adminOpen ? '240px' : '0' }}>
                  {adminSubItems.map((sub) => (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      className={`sidebar-subnav-item ${location.pathname === sub.path ? 'active' : ''}`}
                    >
                      <span className="subnav-dot" />
                      <span>{sub.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {!sidebarCollapsed && (
              <div className="user-info">
                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                <span className="user-role">{user?.role || 'Biller'}</span>
              </div>
            )}
          </div>
          <button
            className="sidebar-logout"
            onClick={handleLogout}
            title="Sign out"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-wrapper">
        {/* Temp password banner */}
        {user?.mustChangePassword && (
          <div className="password-banner">
            <div className="password-banner-content">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                You&apos;re using a temporary password. For security, please{' '}
                <Link to="/settings/account" className="password-banner-link">
                  change your password
                </Link>{' '}
                under Settings &gt; Account.
              </span>
            </div>
          </div>
        )}
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
